// src/pages/purchase-requisition/history-pengajuan/HistoryPengajuan.jsx
import React, { useState, useMemo } from "react";
import {
    Typography, Box, Breadcrumbs, Card, CardContent,
    CircularProgress, Alert, Snackbar,
    IconButton, Stack, Link as MuiLink,
    ClickAwayListener, Tooltip,
    TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/id'; // opsional: untuk bahasa Indonesia
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useOutletContext, useNavigate } from "react-router-dom";
import useAuthCheck from "../../../hooks/useAuthCheck";
import CustomBreadcrumbs from '../../../components/universal-features/breadcrumbs';
import * as XLSX from "xlsx";

import { database } from "../../../firebase";
import { ref, update } from "firebase/database";

// Import custom hooks
import useOrderData from "../hooks/useOrderData";
import useUserTooltipData from "../hooks/useUserTooltipData";
import useOrderSearch from "../hooks/useOrderSearch";

// Import components
import OrderTable from "./components/HistoryOrderTable";
import OrderDetailDialog from "../components/OrderDetailDialog";

// New component for User Tooltip - moved here to be exported for OrderTable
export const UserTooltipDisplay = ({ uid, userName }) => {
    // Each instance of UserTooltipDisplay now uses its own useUserTooltipData hook
    // This import is here because it's used by this component, not HistoryPengajuan directly
    const {
        tooltipOpen,
        tooltipContent,
        tooltipAnchorEl,
        handleTooltipOpen,
        handleTooltipClose
    } = useUserTooltipData();

    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
                open={tooltipOpen}
                onClose={handleTooltipClose}
                anchorEl={tooltipAnchorEl}
                title={
                    <Box sx={{ p: 1 }}>
                        <Typography variant="body2"><strong>Nama:</strong> {tooltipContent.name}</Typography>
                        <Typography variant="body2"><strong>Departemen:</strong> {tooltipContent.department}</Typography>
                        <Typography variant="body2"><strong>Jabatan:</strong> {tooltipContent.jabatan}</Typography>
                        <Typography variant="body2"><strong>Role:</strong> {tooltipContent.roles.join(', ')}</Typography>
                    </Box>
                }
            >
                <span
                    onMouseEnter={(e) => handleTooltipOpen(e, uid)}
                    onMouseLeave={handleTooltipClose}
                    style={{ cursor: 'pointer' }}
                >
                    {userName}
                </span>
            </Tooltip>
        </ClickAwayListener>
    );
};

// Dialog untuk menambahkan/mengedit No PR dan Tanggal Creat
const AddPrDialog = ({ open, onClose, order, onSave }) => {
    // Dapatkan tanggal saat ini untuk default (tanpa waktu, hanya tanggal)
    const getCurrentDate = () => {
        return dayjs().startOf('day'); // Hanya tanggal, waktu di-set ke 00:00:00
    };

    const [prNumber, setPrNumber] = useState(order?.no_pr || '');
    const [tanggalCreat, setTanggalCreat] = useState(
        order?.tanggal_creat ? dayjs(order.tanggal_creat) : getCurrentDate()
    );
    
    // Tentukan judul berdasarkan apakah No PR sudah ada
    const dialogTitle = order?.no_pr ? "Edit PR" : "Tambahkan PR";

    const handleSave = () => {
        // Panggil fungsi onSave dengan parameter yang diperlukan
        // Tanggal akan dikirim sebagai dayjs object dan waktu akan ditambahkan di handleSavePr
        onSave(order.id, prNumber, tanggalCreat);
        onClose();
    };

    // Reset form ketika dialog dibuka
    React.useEffect(() => {
        if (open) {
            setPrNumber(order?.no_pr || '');
            setTanggalCreat(
                order?.tanggal_creat ? dayjs(order.tanggal_creat) : getCurrentDate()
            );
        }
    }, [open, order]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="No PR"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={prNumber}
                    onChange={(e) => setPrNumber(e.target.value)}
                    placeholder="Masukkan nomor PR"
                    sx={{ mt: 2 }}
                />
                
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Tanggal Creat"
                        value={tanggalCreat}
                        onChange={(newValue) => setTanggalCreat(newValue)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                margin: "dense",
                                variant: "outlined",
                                sx: { mt: 2 }
                            }
                        }}
                        format="DD/MM/YYYY"
                    />
                </LocalizationProvider>
            </DialogContent>
            <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                <Button onClick={onClose}>Batal</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Simpan
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Komponen untuk menampilkan ID Order dengan icon copy
const OrderIdDisplay = ({ orderId }) => {
    const [copyTooltip, setCopyTooltip] = useState("Copy ID Order");

    const handleCopyId = () => {
        navigator.clipboard.writeText(orderId)
            .then(() => {
                setCopyTooltip("ID Order dicopy!");
                setTimeout(() => setCopyTooltip("Copy ID Order"), 2000);
            })
            .catch((err) => {
                console.error("Gagal menyalin ID Order:", err);
                setCopyTooltip("Gagal menyalin!");
                setTimeout(() => setCopyTooltip("Copy ID Order"), 2000);
            });
    };

    return (
        <Tooltip title={copyTooltip} placement="top">
            <IconButton
                size="small"
                onClick={handleCopyId}
                sx={{ 
                    padding: '4px',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                <ContentCopyIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

function HistoryPengajuan() {
    const { user, loading: authLoading, userProfile } = useAuthCheck();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Use custom hook for order data
    const {
        orders,
        loadingOrders,
        flowConfigurations,
        loadingFlows,
        adminJabatanId,
        loadingAdminJabatanId,
        uomList,
        loadingUom,
        prcnInvMaterialPackPHRoleId,
        invManagementFreshPineOtherRoleId,
        costAnalystFreshPineOtherRoleId, // DAPATKAN DARI useOrderData
        developerRoleId, // DAPATKAN DARI useOrderData
        loadingRoles,
    } = useOrderData(user, userProfile);

    // Filter only approved orders for history
    const approvedOrders = useMemo(() => {
        return orders.filter(order => order.status === "Approved");
    }, [orders]);

    // Use custom hook for order search
    const {
        searchTerm,
        setSearchTerm,
        filteredOrders,
    } = useOrderSearch(approvedOrders); // Pass the approved orders to the search hook

    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [openPrDialog, setOpenPrDialog] = useState(false);
    const [selectedOrderForPr, setSelectedOrderForPr] = useState(null);

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedOrder(null);
    };

    const handleAddPr = (order) => {
        const userRoles = userProfile?.id_role ? userProfile.id_role.split(';') : [];
        const canEdit = userRoles.includes(prcnInvMaterialPackPHRoleId) || 
                       userRoles.includes(invManagementFreshPineOtherRoleId) ||
                       userRoles.includes(developerRoleId);
        
        if (!canEdit) {
            setSnackbar({ open: true, message: "Anda tidak memiliki izin untuk menambahkan No PR.", severity: "error" });
            return;
        }
        
        setSelectedOrderForPr(order);
        setOpenPrDialog(true);
    };

    const handleClosePrDialog = () => {
        setOpenPrDialog(false);
        setSelectedOrderForPr(null);
    };

    const handleSavePr = async (orderId, prNumber, tanggalCreat) => {
        try {
            // Dapatkan tanggal dan waktu saat ini
            const currentDateTime = dayjs();
            
            // Format untuk penyimpanan
            let tanggalUntukDisimpan;
            
            if (tanggalCreat && dayjs.isDayjs(tanggalCreat)) {
                // Jika tanggalCreat adalah dayjs object, gunakan tanggal dari input dan waktu dari saat ini
                tanggalUntukDisimpan = tanggalCreat
                    .hour(currentDateTime.hour())
                    .minute(currentDateTime.minute())
                    .second(currentDateTime.second())
                    .format('YYYY-MM-DDTHH:mm:ss');
            } else if (tanggalCreat) {
                // Jika tanggalCreat adalah string, parse dulu
                try {
                    const parsedDate = dayjs(tanggalCreat);
                    if (parsedDate.isValid()) {
                        tanggalUntukDisimpan = parsedDate
                            .hour(currentDateTime.hour())
                            .minute(currentDateTime.minute())
                            .second(currentDateTime.second())
                            .format('YYYY-MM-DDTHH:mm:ss');
                    } else {
                        // Jika parsing gagal, gunakan tanggal dan waktu saat ini
                        tanggalUntukDisimpan = currentDateTime.format('YYYY-MM-DDTHH:mm:ss');
                    }
                } catch (error) {
                    console.error("Error parsing date:", error);
                    tanggalUntukDisimpan = currentDateTime.format('YYYY-MM-DDTHH:mm:ss');
                }
            } else {
                // Jika tanggalCreat kosong, gunakan tanggal dan waktu saat ini
                tanggalUntukDisimpan = currentDateTime.format('YYYY-MM-DDTHH:mm:ss');
            }
            
            console.log('Menyimpan tanggal dengan waktu:', tanggalUntukDisimpan);
            
            // Update data di Firebase
            const orderRef = ref(database, `db_scm/tb_order_barang_expand/${orderId}`);
            await update(orderRef, {
                no_pr: prNumber,
                tanggal_creat: tanggalUntukDisimpan  // Simpan sebagai tanggal_creat dengan waktu
            });
            
            setSnackbar({ open: true, message: "No PR dan Tanggal Creat berhasil disimpan!", severity: "success" });
            
        } catch (error) {
            console.error("Error saving PR number:", error);
            setSnackbar({ open: true, message: "Gagal menyimpan No PR: " + error.message, severity: "error" });
        }
    };

    // Fungsi untuk menghitung interval waktu dengan format baru
    const calculateInterval = (order) => {
        if (!order || !order.created_at || order.status !== "Approved") {
            return "-";
        }

        try {
            // Waktu pengajuan (created_at)
            const submissionTime = new Date(order.created_at);
            
            // Cari waktu approval terakhir
            let approvalTime = submissionTime; // default
            
            if (order.approval_history && order.approval_history.length > 0) {
                // Ambil approval terakhir dengan decision "Approved"
                const approvedEntries = order.approval_history.filter(entry => 
                    entry.decision === "Approved"
                );
                
                if (approvedEntries.length > 0) {
                    // Urutkan dari yang terbaru ke terlama
                    approvedEntries.sort((a, b) => 
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                    const finalApproval = approvedEntries[0];
                    if (finalApproval.timestamp) {
                        approvalTime = new Date(finalApproval.timestamp);
                    }
                }
            } else if (order.updated_at) {
                // Fallback: gunakan updated_at jika tidak ada approval_history
                approvalTime = new Date(order.updated_at);
            }
            
            // Hitung selisih dalam milidetik
            const diffMs = approvalTime - submissionTime;
            
            if (diffMs <= 0) return "0 D";
            
            // Hitung total detik
            const totalSeconds = Math.floor(diffMs / 1000);
            const days = Math.floor(totalSeconds / 86400);
            const remainingSeconds = totalSeconds % 86400;
            const hours = Math.floor(remainingSeconds / 3600);
            const remainingMinutesSeconds = remainingSeconds % 3600;
            const minutes = Math.floor(remainingMinutesSeconds / 60);
            const seconds = remainingMinutesSeconds % 60;
            
            // Format output baru: J untuk jam, M untuk menit, D untuk detik
            let result = [];
            if (days > 0) {
                result.push(`${days} H`); // H untuk hari
            }
            if (hours > 0) {
                result.push(`${hours} J`); // J untuk jam
            }
            if (minutes > 0) {
                result.push(`${minutes} M`); // M untuk menit
            }
            if (seconds > 0) {
                result.push(`${seconds} D`); // D untuk detik
            }
            
            return result.length > 0 ? result.join(', ') : "0 D";
            
        } catch (error) {
            console.error("Error calculating interval:", error);
            return "-";
        }
    };

    // Helper function to get UoM name from ID (added here)
    const getUomName = (uomId) => {
        if (!Array.isArray(uomList)) {
            return uomId;
        }
        const uom = uomList.find(item => item.id === uomId);
        return uom ? uom.nama_uom : uomId;
    };

    // Helper function to format tanggal creat for display
    const formatTanggalCreat = (tanggalCreat) => {
        if (!tanggalCreat) return '-';
        
        try {
            // Parsing tanggal menggunakan dayjs
            const date = dayjs(tanggalCreat);
            
            if (!date.isValid()) {
                return tanggalCreat; // Return as-is jika parsing gagal
            }
            
            // Format: DD/MM/YYYY
            return date.format('DD/MM/YYYY');
        } catch (error) {
            console.error("Error formatting tanggal creat:", error);
            return tanggalCreat;
        }
    };

    // Function to download material item data to Excel
    const handleDownloadExcel = (order) => {
        if (!order || !order.item_details || order.item_details.length === 0) {
            setSnackbar({ open: true, message: "Tidak ada data item material untuk diunduh.", severity: "warning" });
            return;
        }

        const ws_data = [];
        const header = [
            "Kode Material",
            "Deskripsi Material",
            "QTY",
            "UoM",
            "Valuation Price",
            "Jumlah Total",
            "Cost Center",
            "Noted Kebutuhan",
        ];
        ws_data.push(header);

        const ws = XLSX.utils.aoa_to_sheet(ws_data); // Create empty sheet with header row

        // Add data rows with cell formatting
        order.item_details.forEach((item, index) => {
            const row = [
                item.material_code,
                item.material_description,
                item.quantity_jul,
                getUomName(item.unit_of_measure),
                parseFloat(String(item.valuation_price).replace(/[^0-9]/g, '')), // Store as number for formatting
                parseFloat(String(item.total_amount).replace(/[^0-9]/g, '')), // Store as number for formatting
                item.cost_center,
                item.notes,
            ];
            XLSX.utils.sheet_add_aoa(ws, [row], { origin: -1 }); // Append row

            // Apply number formatting based on data type and UoM
            const rowIndex = index + 1; // 0-indexed for data, 1-indexed for Excel rows
            const qtyCell = XLSX.utils.encode_cell({ r: rowIndex, c: 2 }); // Column C (QTY)
            const valuationPriceCell = XLSX.utils.encode_cell({ r: rowIndex, c: 4 }); // Column E (Harga Valuasi)
            const totalAmountCell = XLSX.utils.encode_cell({ r: rowIndex, c: 5 }); // Column F (Jumlah Total)

            // QTY formatting
            const uomName = getUomName(item.unit_of_measure);
            if (typeof item.quantity_jul === 'number' && !isNaN(item.quantity_jul)) {
                // Check if UoM is a common non-numeric unit, otherwise assume number
                if (uomName.toLowerCase() === 'pcs' || uomName.toLowerCase() === 'unit' || uomName.toLowerCase() === 'buah') {
                    if (ws[qtyCell]) ws[qtyCell].t = 's'; // Set type to string if UoM is non-numeric
                } else {
                    if (ws[qtyCell]) ws[qtyCell].t = 'n'; // Set type to number
                    if (ws[qtyCell]) ws[qtyCell].z = '#,##0'; // Number format without decimals
                }
            } else {
                if (ws[qtyCell]) ws[qtyCell].t = 's'; // Default to string if not a valid number
            }

            // Valuation Price and Total Amount formatting (always number with thousand separators)
            if (ws[valuationPriceCell]) {
                ws[valuationPriceCell].t = 'n'; // Set type to number
                ws[valuationPriceCell].z = '#,##0'; // Number format without decimals
            }
            if (ws[totalAmountCell]) {
                ws[totalAmountCell].t = 'n'; // Set type to number
                ws[totalAmountCell].z = '#,##0'; // Number format without decimals
            }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Detail Item Material");

        // Create file name
        const fileName = `Order_${order.id}_Item_Details.xlsx`;

        // Download file
        XLSX.writeFile(wb, fileName);
        setSnackbar({ open: true, message: "Data item material berhasil diunduh!", severity: "success" });
    };

    if (authLoading || loadingOrders || loadingFlows || loadingAdminJabatanId || loadingUom || loadingRoles) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini.
                </Alert>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 0 }}>
                <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

                <Card>
                    <CardContent sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="h6" component="h2">
                                History Pengajuan
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {/* Search Input */}
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Cari order..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: { xs: '100%', sm: 'auto' }, mr: 1 }}
                                />
                            </Stack>
                        </Stack>

                        <OrderTable
                            orders={filteredOrders}
                            flowConfigurations={flowConfigurations}
                            user={user}
                            userProfile={userProfile}
                            prcnInvMaterialPackPHRoleId={prcnInvMaterialPackPHRoleId}
                            invManagementFreshPineOtherRoleId={invManagementFreshPineOtherRoleId}
                            costAnalystFreshPineOtherRoleId={costAnalystFreshPineOtherRoleId}
                            developerRoleId={developerRoleId} 
                            handleViewOrder={handleViewOrder}
                            handleAddPr={handleAddPr}
                            handleDownloadExcel={handleDownloadExcel}
                            getUomName={getUomName}
                            formatTanggalCreat={formatTanggalCreat}
                            calculateInterval={calculateInterval}
                            OrderIdDisplay={OrderIdDisplay}
                        />
                    </CardContent>
                </Card>

                {/* Render OrderDetailDialog component */}
                <OrderDetailDialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    selectedOrder={selectedOrder}
                    flowConfigurations={flowConfigurations}
                    uomList={uomList}
                />

                {/* Dialog untuk menambahkan No PR */}
                {selectedOrderForPr && (
                    <AddPrDialog
                        open={openPrDialog}
                        onClose={handleClosePrDialog}
                        order={selectedOrderForPr}
                        onSave={handleSavePr}
                    />
                )}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
}

export default HistoryPengajuan;